import { useEffect, useRef, useState } from "react";
import AIToolsAPI from "../../../api/AIToolsAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../../utils/GeneralUtils";
import { AppConfirm } from "../../../../utils/PopupUtil";

export default function useAskYourPDF() {
  const [uploading, setUploading] = useState(false);
  const [querying, setQuerying] = useState(false);
  const [dbName, setDbName] = useState("");
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");

  const ref = useRef<any>({ fileName, dbName });
  ref.current.fileName = fileName;
  ref.current.dbName = dbName;

  // Cleanup session on dismount.
  useEffect(() => {
    return () => {
      deletePDF();
    };
  }, []);

  // Uploading a PDF to Nasser's AI server to be able to be queried.
  async function uploadPDF(file: File, onError: () => void) {
    setUploading(true);
    setError("");

    try {
      const data = await AIToolsAPI.askYourPDFUpload(file).then(
        (data) => data?.data?.data?.data
      );

      if (!data.filename || !data.db_name) throw undefined;

      setFileName(data.filename);
      setDbName(data.db_name);
    } catch (err) {
      callFunctionIfExists(onError);
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  // Querying an already uploaded PDF file on Nasser's server.
  async function queryPDF(query: string, onSessionEnded: () => void) {
    setQuerying(true);
    setError("");
    let result = "";

    try {
      const data = await AIToolsAPI.askYourPDFGetData(query, dbName);

      result = data?.data?.data?.data;
    } catch (err) {
      const message = getErrorMessage(err);

      if (message === "Session has ended") {
        AppConfirm({
          title: "Session Ended",
          text: "Sorry, but your file session has ended. Upload another file to start again.",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
        setFileName("");
        setDbName("");
        onSessionEnded();
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setQuerying(false);
    }

    return result;
  }

  // Deleting a file at end of session on Nassers Server.
  async function deletePDF() {
    setError("");
    const fileName: string = ref.current.fileName;
    const dbName: string = ref.current.dbName;

    if (!fileName || !dbName) return;

    try {
      await AIToolsAPI.askYourPDFDelete(fileName, dbName);
    } finally {
      setFileName("");
      setDbName("");
    }
  }

  return {
    uploading: uploading,
    querying: querying,
    canChat: !!dbName && !!fileName,
    uploadPDF: uploadPDF,
    queryPDF: queryPDF,
    deletePDF: deletePDF,
    error: error,
  };
}
